<script setup>
import { formatCurrency } from '@core/utils/formatters'
import {
  pricingPlanLifecycleColor,
  pricingPlanLifecycleLabel,
  pricingPlanTypeLabel,
  usePricingPlansStore,
} from '@/stores/pricingPlans'

definePage({
  meta: {
    navActiveLink: 'pricing-plans',
  },
})

const store = usePricingPlansStore()
const router = useRouter()
const { t } = useI18n()

const createDialog = ref(false)
const createStep = ref(1)
const formError = ref('')
const formSuccess = ref('')

const createForm = ref({
  plan_type: '',
  name: '',
  description: '',
  price_euros: null,
  billing_cadence: 'monthly',
  session_count: 10,
  validity_days: 90,
})

onMounted(() => {
  store.fetchPricingPlans().catch(() => null)
})

const applyFilters = () => {
  store.fetchPricingPlans().catch(() => null)
}

const openPlan = id => {
  router.push({ name: 'pricing-plans-id', params: { id } })
}

const openCreate = () => {
  formError.value = ''
  createStep.value = 1
  createForm.value = {
    plan_type: '',
    name: '',
    description: '',
    price_euros: null,
    billing_cadence: 'monthly',
    session_count: 10,
    validity_days: 90,
  }
  createDialog.value = true
}

const selectType = type => {
  createForm.value.plan_type = type
  createStep.value = 2
}

const eurosToCents = euros => Math.round(Number(euros) * 100)

const submitCreate = async () => {
  formError.value = ''
  formSuccess.value = ''

  if (!createForm.value.name || createForm.value.price_euros == null) {
    formError.value = t('pricingPlans.errors.requiredFields')

    return
  }

  const payload = {
    name: createForm.value.name,
    description: createForm.value.description || null,
    plan_type: createForm.value.plan_type,
    price_cents: eurosToCents(createForm.value.price_euros),
    currency: 'EUR',
  }

  if (createForm.value.plan_type === 'subscription')
    payload.billing_cadence = createForm.value.billing_cadence
  if (createForm.value.plan_type === 'session_package') {
    if (!createForm.value.session_count || !createForm.value.validity_days) {
      formError.value = t('pricingPlans.errors.packageFields')

      return
    }
    payload.session_count = Number(createForm.value.session_count)
    payload.validity_days = Number(createForm.value.validity_days)
  }

  try {
    const plan = await store.createPricingPlan(payload)

    createDialog.value = false
    formSuccess.value = t('pricingPlans.created')
    await router.push({ name: 'pricing-plans-id', params: { id: plan.id } })
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const planSummary = plan => {
  const price = formatCurrency(plan.price_cents, plan.currency || 'EUR')
  if (plan.plan_type === 'subscription')
    return `${price} / ${t(`pricingPlans.cadences.${plan.billing_cadence}`)}`
  if (plan.plan_type === 'session_package') {
    return `${price} · ${t('pricingPlans.sessionCountShort', { count: plan.session_count })} · ${t('pricingPlans.validityDaysShort', { days: plan.validity_days })}`
  }

  return `${price} / ${t('pricingPlans.perSession')}`
}
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-6">
      <div>
        <h1 class="text-h4 mb-1">
          {{ $t('pricingPlans.title') }}
        </h1>
        <p class="text-medium-emphasis mb-0">
          {{ $t('pricingPlans.subtitle') }}
        </p>
      </div>

      <VBtn
        color="primary"
        :disabled="store.saving"
        @click="openCreate"
      >
        {{ $t('pricingPlans.create') }}
      </VBtn>
    </div>

    <VAlert
      v-if="formSuccess"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ formSuccess }}
    </VAlert>

    <VAlert
      v-if="store.error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ store.error }}
    </VAlert>

    <VCard
      flat
      border
      class="mb-4"
    >
      <VCardText class="d-flex flex-wrap gap-4">
        <VSelect
          v-model="store.filters.lifecycle_state"
          :label="$t('pricingPlans.statusLabel')"
          density="comfortable"
          hide-details
          clearable
          style="max-inline-size: 14rem;"
          :items="[
            { title: $t('pricingPlans.statusAll'), value: '' },
            { title: $t('pricingPlans.status.draft'), value: 'draft' },
            { title: $t('pricingPlans.status.published'), value: 'published' },
            { title: $t('pricingPlans.status.retired'), value: 'retired' },
          ]"
          @update:model-value="applyFilters"
        />
        <VSelect
          v-model="store.filters.plan_type"
          :label="$t('pricingPlans.typeLabel')"
          density="comfortable"
          hide-details
          clearable
          style="max-inline-size: 16rem;"
          :items="[
            { title: $t('pricingPlans.typeAll'), value: '' },
            { title: $t('pricingPlans.planTypes.subscription'), value: 'subscription' },
            { title: $t('pricingPlans.planTypes.session_package'), value: 'session_package' },
            { title: $t('pricingPlans.planTypes.hourly'), value: 'hourly' },
          ]"
          @update:model-value="applyFilters"
        />
      </VCardText>
    </VCard>

    <div
      v-if="store.loading"
      class="text-medium-emphasis"
      role="status"
    >
      {{ $t('pricingPlans.loading') }}
    </div>

    <VAlert
      v-else-if="store.isEmpty"
      type="info"
      variant="tonal"
      role="status"
    >
      {{ $t('pricingPlans.empty') }}
    </VAlert>

    <VList
      v-else
      lines="two"
      class="bg-transparent"
    >
      <VListItem
        v-for="plan in store.pricingPlans"
        :key="plan.id"
        :title="plan.name"
        :subtitle="`${pricingPlanTypeLabel(plan.plan_type)} · ${planSummary(plan)}`"
        class="px-0"
        @click="openPlan(plan.id)"
      >
        <template #append>
          <VChip
            size="small"
            label
            :color="pricingPlanLifecycleColor(plan.lifecycle_state)"
            class="ms-2"
          >
            <span class="visually-hidden">{{ $t('pricingPlans.statusPrefix') }}</span>
            {{ pricingPlanLifecycleLabel(plan.lifecycle_state) }}
          </VChip>
          <VIcon
            icon="ri-arrow-right-s-line"
            class="ms-2"
          />
        </template>
      </VListItem>
    </VList>

    <VDialog
      v-model="createDialog"
      max-width="560"
      aria-modal="true"
    >
      <VCard flat>
        <VCardItem>
          <VCardTitle>
            {{ $t('pricingPlans.createDialog.title') }}
          </VCardTitle>
        </VCardItem>
        <VCardText>
          <p class="text-medium-emphasis mb-4">
            {{ createStep === 1 ? $t('pricingPlans.createDialog.pickType') : $t('pricingPlans.createDialog.fillDetails') }}
          </p>

          <VAlert
            v-if="formError"
            type="error"
            variant="tonal"
            class="mb-4"
            role="alert"
          >
            {{ formError }}
          </VAlert>

          <div
            v-if="createStep === 1"
            class="d-flex flex-column gap-3"
            role="group"
            :aria-label="$t('pricingPlans.createDialog.pickType')"
          >
            <div
              v-for="type in ['subscription', 'session_package', 'hourly']"
              :key="type"
              class="d-flex align-center gap-2"
            >
              <VBtn
                variant="tonal"
                class="flex-grow-1 justify-start text-none"
                @click="selectType(type)"
              >
                {{ $t(`pricingPlans.planTypes.${type}`) }}
              </VBtn>
              <VTooltip location="top">
                <template #activator="{ props: tooltipProps }">
                  <IconBtn
                    v-bind="tooltipProps"
                    :aria-label="$t('pricingPlans.planTypeHintAria', {
                      type: $t(`pricingPlans.planTypes.${type}`),
                    })"
                  >
                    <VIcon
                      icon="ri-information-line"
                      size="20"
                    />
                  </IconBtn>
                </template>
                <span>{{ $t(`pricingPlans.planTypeHints.${type}`) }}</span>
              </VTooltip>
            </div>
          </div>

          <div
            v-else
            class="d-flex flex-column gap-4"
          >
            <VChip
              size="small"
              label
              color="info"
              class="align-self-start"
            >
              {{ pricingPlanTypeLabel(createForm.plan_type) }}
            </VChip>

            <VTextField
              v-model="createForm.name"
              :label="$t('pricingPlans.fields.name')"
            />
            <VTextarea
              v-model="createForm.description"
              :label="$t('pricingPlans.fields.description')"
              rows="2"
              auto-grow
            />
            <VTextField
              v-model.number="createForm.price_euros"
              :label="$t('pricingPlans.fields.price')"
              type="number"
              min="0.01"
              step="0.01"
              prefix="€"
            />
            <VSelect
              v-if="createForm.plan_type === 'subscription'"
              v-model="createForm.billing_cadence"
              :label="$t('pricingPlans.fields.billingCadence')"
              :items="[
                { title: $t('pricingPlans.cadences.monthly'), value: 'monthly' },
                { title: $t('pricingPlans.cadences.quarterly'), value: 'quarterly' },
                { title: $t('pricingPlans.cadences.semiannual'), value: 'semiannual' },
                { title: $t('pricingPlans.cadences.annual'), value: 'annual' },
              ]"
            />
            <VTextField
              v-if="createForm.plan_type === 'session_package'"
              v-model.number="createForm.session_count"
              :label="$t('pricingPlans.fields.sessionCount')"
              type="number"
              min="1"
              step="1"
            />
            <VTextField
              v-if="createForm.plan_type === 'session_package'"
              v-model.number="createForm.validity_days"
              :label="$t('pricingPlans.fields.validityDays')"
              type="number"
              min="1"
              step="1"
            />
          </div>
        </VCardText>
        <VCardActions>
          <VBtn
            v-if="createStep === 2"
            variant="text"
            :disabled="store.saving"
            @click="createStep = 1"
          >
            {{ $t('pricingPlans.createDialog.back') }}
          </VBtn>
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="store.saving"
            @click="createDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            v-if="createStep === 2"
            color="primary"
            :loading="store.saving"
            @click="submitCreate"
          >
            {{ $t('pricingPlans.createDialog.submit') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.visually-hidden {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
